import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';
import { getArticleBySlug, pillarArticles } from '@/mocks/articles';
import { SITE_URL } from '@/lib/site';

export default function Post() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <main className="pt-16">
          <div className="mx-auto w-full max-w-3xl px-4 py-24 text-center md:px-6">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-stone-100 text-2xl text-stone-400">
              <i className="ri-question-line"></i>
            </span>
            <h1 className="mt-6 font-heading text-2xl font-bold text-stone-900">
              Article not found
            </h1>
            <p className="mt-3 text-sm text-stone-500">
              The article you are looking for does not exist or may have moved.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
            >
              <i className="ri-arrow-left-line"></i>
              {t('cta.backHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const related = pillarArticles.filter((a) => a.slug !== article.slug).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    dateModified: '2026-08-20',
    inLanguage: 'en',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.title,
        item: `${SITE_URL}/article/${article.slug}`,
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-cream">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <Navbar />

      <main className="pt-16">
        <article>
          {/* Hero image */}
          <div className="relative h-[240px] overflow-hidden md:h-[340px]">
            <img
              src={article.image}
              alt={article.imageAlt}
              title={`${article.title} — USFans`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/30" />
          </div>

          <div className="mx-auto w-full max-w-4xl px-4 py-10 md:px-6 md:py-14">
            {/* Breadcrumb */}
            <nav className="flex flex-wrap items-center gap-1.5 text-sm text-stone-500">
              <Link to="/" className="transition-colors hover:text-emerald-600">
                {t('breadcrumb.home')}
              </Link>
              <span className="flex h-4 w-4 items-center justify-center text-xs text-stone-300">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-stone-400">{article.title}</span>
            </nav>

            {/* Header */}
            <header className="mt-6">
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                <i className="ri-article-line"></i>
                USFans Guide
              </span>
              <h1 className="mt-4 font-heading text-2xl font-extrabold leading-tight text-stone-900 md:text-4xl">
                {article.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-stone-400">
                <span className="inline-flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center text-sm">
                    <i className="ri-calendar-line"></i>
                  </span>
                  Last updated: {article.updated}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center text-sm">
                    <i className="ri-time-line"></i>
                  </span>
                  {article.readingTime}
                </span>
              </div>
            </header>

            <p className="mt-6 text-base leading-relaxed text-stone-600 md:text-lg">{article.intro}</p>

            {/* Table of contents */}
            <nav className="mt-8 rounded-lg border border-stone-200 bg-white p-5">
              <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-stone-900">
                {t('toc.title')}
              </h2>
              <ul className="mt-3 space-y-2">
                {article.sections.map((s, i) => (
                  <li key={s.heading}>
                    <a
                      href={`#section-${i}`}
                      className="inline-flex items-center gap-2 text-sm text-stone-600 transition-colors hover:text-emerald-600"
                    >
                      <span className="flex h-4 w-4 items-center justify-center text-xs text-emerald-500">
                        <i className="ri-arrow-right-s-line"></i>
                      </span>
                      {s.heading}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Sections */}
            {article.sections.map((s, i) => (
              <section key={s.heading} id={`section-${i}`} className="mt-10">
                <h2 className="font-heading text-xl font-bold text-stone-900 md:text-2xl">
                  {s.heading}
                </h2>
                {s.paragraphs.map((p, j) => (
                  <p key={j} className="mt-4 text-base leading-relaxed text-stone-600">
                    {p}
                  </p>
                ))}
              </section>
            ))}

            {/* FAQ */}
            <section className="mt-12">
              <h2 className="font-heading text-xl font-bold text-stone-900 md:text-2xl">
                {t('faq.title')}
              </h2>
              <div className="mt-5 space-y-3">
                {article.faq.map((f, i) => (
                  <div key={i} className="overflow-hidden rounded-lg border border-stone-200 bg-white">
                    <details className="group">
                      <summary className="flex w-full cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-stone-50">
                        <span className="text-sm font-semibold text-stone-800">{f.q}</span>
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-stone-100 text-base text-stone-500 transition-colors group-open:bg-emerald-600 group-open:text-white">
                          <i className="ri-add-line group-open:hidden"></i>
                          <i className="ri-subtract-line hidden group-open:block"></i>
                        </span>
                      </summary>
                      <div className="px-5 pb-5">
                        <p className="text-sm leading-relaxed text-stone-600">{f.a}</p>
                      </div>
                    </details>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="mt-12 rounded-lg bg-emerald-50 p-8 text-center">
              <h2 className="font-heading text-xl font-bold text-stone-900 md:text-2xl">
                {t('cta.title')}
              </h2>
              <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-stone-600 md:text-base">
                {t('cta.body')}
              </p>
              <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-emerald-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 sm:w-auto"
                >
                  {t('cta.primary')}
                  <i className="ri-external-link-line"></i>
                </a>
                <Link
                  to="/"
                  className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-emerald-300 bg-white px-6 py-3 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 sm:w-auto"
                >
                  {t('cta.backHome')}
                </Link>
              </div>
            </section>

            {/* Safety note */}
            <div className="mt-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-amber-600">
                <i className="ri-shield-check-line"></i>
              </span>
              <p className="text-xs leading-relaxed text-amber-800 md:text-sm">{t('safetyNote')}</p>
            </div>

            {/* Related articles */}
            <section className="mt-12">
              <h2 className="font-heading text-xl font-bold text-stone-900 md:text-2xl">
                {t('related.title')}
              </h2>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/article/${r.slug}`}
                    className="group overflow-hidden rounded-lg border border-stone-200 bg-white transition-colors hover:border-emerald-300"
                  >
                    <img
                      src={r.image}
                      alt={r.imageAlt}
                      className="h-32 w-full object-cover object-top"
                    />
                    <div className="p-5">
                      <h3 className="font-heading text-sm font-semibold leading-snug text-stone-900">
                        {r.title}
                      </h3>
                      <span className="mt-3 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-emerald-600">
                        {t('cta.readArticle')}
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="mt-8 text-center">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-stone-300 bg-white px-5 py-2.5 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50"
                >
                  <i className="ri-arrow-left-line"></i>
                  {t('cta.backHome')}
                </Link>
              </div>
            </section>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}